import {skipToken} from '@reduxjs/toolkit/query';
import {Choice, DataStatus, IconButton, TextInput} from 'platform/components';
import {Box, HStack, Show, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useRef, useState} from 'react';

import {isNil, omit} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {
  AdditionalInformationOption,
  AdditionalInformationValueType,
  useGetServiceVehicleAdditionalInformationQuery,
  useGetServiceVehicleV2Query,
  usePatchServiceVehicleAdditionalInformationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {EMPTY_PLACEHOLDER, getSafely, parseDate} from 'shared/web';

interface VehicleAdditionalInformationProps {
  vehicleId: string;
}

type DateTimeFormatter = ReturnType<typeof useDateTimeFormatter>;

const OPTION_LABEL_SEPARATOR = ' → ';

const getTranslationKey = (code: string | null, type: 'label' | 'description'): string => {
  if (isNil(code)) {
    return '';
  }

  const normalizedCode = code.charAt(0).toLowerCase() + code.slice(1);

  return `entity.vehicle.additionalInformation.${type}s.${normalizedCode}`;
};

const getFieldTranslation = (code: string | null, type: 'label' | 'description'): string => {
  const translationKey = getTranslationKey(code, type);
  const translated = i18n.t(translationKey);

  if (translated !== translationKey) {
    return translated;
  }

  return translationKey;
};

const getStringValue = (value: Record<string, unknown> | null): string => {
  if (isNil(value)) {
    return '';
  }

  if (typeof value === 'object' && 'value' in value) {
    return String(value.value);
  }

  return String(value);
};

const toChoiceOption = (o: AdditionalInformationOption) => ({
  value: o.value,
  label: isNotNil(o.description) ? `${o.value}${OPTION_LABEL_SEPARATOR}${o.description}` : o.value,
});

const formatValue = (
  formatDateTime: DateTimeFormatter,
  value: Record<string, unknown> | null,
  valueType: AdditionalInformationValueType,
  options?: AdditionalInformationOption[] | null
): string | null => {
  if (isNil(value)) {
    return null;
  }

  return match(valueType)
    .with('string', () => (value ? getStringValue(value) : EMPTY_PLACEHOLDER))
    .with('date', () => formatDateTime('dateShort', parseDate(getStringValue(value))))
    .with('dateTime', () => formatDateTime('dateTimeShort', parseDate(getStringValue(value))))
    .with('boolean', () =>
      value['value'] === true ? i18n.t('general.labels.yes') : i18n.t('general.labels.no')
    )
    .with('dropdown', () => {
      const stringValue = getStringValue(value);
      const found = options?.find((o) => o.value === stringValue);
      return found?.description ?? found?.value ?? null;
    })
    .exhaustive();
};

export function VehicleAdditionalInformation(props: VehicleAdditionalInformationProps) {
  const formatDateTime = useDateTimeFormatter();

  const {
    data: serviceVehicle,
    isLoading: isServiceVehicleLoading,
    isError: isServiceVehicleError,
  } = useGetServiceVehicleV2Query({vehicleId: props.vehicleId});

  const serviceVehicleId = serviceVehicle?.serviceVehicleId;

  const {
    data: additionalInformation,
    isFetching: isAdditionalInformationFetching,
    isLoading: isAdditionalInformationLoading,
    isError: isAdditionalInformationError,
  } = useGetServiceVehicleAdditionalInformationQuery(
    isNotNil(serviceVehicleId) ? {serviceVehicleId} : skipToken
  );

  const stableItemsRef = useRef<typeof additionalInformation>(undefined);
  if (isNotNil(additionalInformation)) {
    stableItemsRef.current = additionalInformation;
  }

  const [patchAdditionalInformation, {isLoading: isPatching}] =
    usePatchServiceVehicleAdditionalInformationMutation();

  const [editingStates, setEditingStates] = useState<Record<string, string | null>>({});
  const [pendingValues, setPendingValues] = useState<Record<string, string | null>>({});

  const isLoading =
    isServiceVehicleLoading || (isAdditionalInformationLoading && isNil(stableItemsRef.current));
  const isError = isServiceVehicleError || isAdditionalInformationError;

  const items = stableItemsRef.current ?? [];

  const handleCancel = (itemCode: string | null) => {
    if (isNil(itemCode)) {
      return;
    }

    setEditingStates((prev) => omit([itemCode], prev));
  };

  const handleSave = (itemCode: string) => {
    const item = items.find((item) => item.code === itemCode);
    if (isNil(item) || isNil(serviceVehicleId) || isNil(item.id)) {
      return;
    }

    const editedValue =
      itemCode in editingStates
        ? (getSafely(editingStates, itemCode) ?? null)
        : getStringValue(item.value);

    setPendingValues((prev) => ({...prev, [itemCode]: editedValue}));

    patchAdditionalInformation({
      serviceVehicleId,
      additionalInformationId: item.id,
      patchAdditionalInformationRequestBody: {value: editedValue ?? ''},
    })
      .unwrap()
      .then(() => {
        setEditingStates((prev) => omit([itemCode], prev));
      })
      .catch((err: Error) => {
        setPendingValues((prev) => omit([itemCode], prev));
        handleApiError(err);
      });
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Box borderRadius="small" boxShadow="elevation_1" backgroundColor="palettes.white.10.100">
        <Box
          paddingHorizontal={4}
          paddingVertical={2}
          backgroundColor="palettes.neutral.10.100"
          borderColor="palettes.neutral.40.100"
          borderBottom="1px solid"
        >
          <HStack width="100%">
            <Box flex={1} borderColor="palettes.neutral.40.100">
              <Text color="secondary">
                {i18n.t('entity.vehicle.additionalInformation.tableHeaders.name')}
              </Text>
            </Box>
            <Box
              flex={1}
              borderLeft="1px solid"
              borderRight="1px solid"
              borderColor="palettes.neutral.40.100"
              paddingHorizontal={4}
            >
              <Text color="secondary">
                {i18n.t('entity.vehicle.additionalInformation.tableHeaders.description')}
              </Text>
            </Box>
            <Box flex={1} borderColor="palettes.neutral.40.100" paddingHorizontal={4}>
              <Text color="secondary">
                {i18n.t('entity.vehicle.additionalInformation.tableHeaders.value')}
              </Text>
            </Box>
          </HStack>
        </Box>

        {items.map((item) => (
          <Box
            paddingVertical={2}
            key={item.code}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            <HStack width="100%" align="center">
              <Box paddingLeft={4} flex={1} borderColor="palettes.neutral.40.100">
                {getFieldTranslation(item.code, 'label')}
              </Box>
              <Box flex={1} borderColor="palettes.neutral.40.100">
                {getFieldTranslation(item.code, 'description')}
              </Box>
              <Box paddingHorizontal={4} flex={1} borderColor="palettes.neutral.40.100">
                <Show when={item.isEditable}>
                  <HStack spacing={2} align="center">
                    <Box flex={1}>
                      {match(item.valueType)
                        .with('dropdown', () => {
                          const rawValue = getStringValue(item.value);
                          const fromServer = rawValue !== '' ? rawValue : null;
                          const hasPending =
                            isAdditionalInformationFetching &&
                            isNotNil(item.code) &&
                            item.code in pendingValues;
                          const fallbackValue =
                            hasPending && isNotNil(item.code)
                              ? (getSafely(pendingValues, item.code) ?? null)
                              : fromServer;
                          const currentValue =
                            isNotNil(item.code) && item.code in editingStates
                              ? (getSafely(editingStates, item.code) ?? null)
                              : fallbackValue;
                          return (
                            <Choice<string>
                              value={currentValue}
                              onChange={(val) => {
                                const itemCode = item.code;
                                if (isNotNil(itemCode)) {
                                  setEditingStates((prev) => ({...prev, [itemCode]: val}));
                                }
                              }}
                              options={(item.options ?? []).map(toChoiceOption)}
                              isDisabled={isPatching}
                            />
                          );
                        })
                        .otherwise(() => (
                          <TextInput
                            value={(() => {
                              if (isNotNil(item.code) && item.code in editingStates) {
                                return getSafely(editingStates, item.code) ?? '';
                              }
                              if (
                                isAdditionalInformationFetching &&
                                isNotNil(item.code) &&
                                item.code in pendingValues
                              ) {
                                return getSafely(pendingValues, item.code) ?? '';
                              }
                              return getStringValue(item.value);
                            })()}
                            onChange={(value) => {
                              const itemCode = item.code;
                              if (isNotNil(itemCode)) {
                                setEditingStates((prev) => ({
                                  ...prev,
                                  [itemCode]: value ?? '',
                                }));
                              }
                            }}
                            isDisabled={isPatching}
                          />
                        ))}
                    </Box>
                    <IconButton
                      icon="content/clear"
                      onClick={() => {
                        if (isNotNil(item.code)) {
                          handleCancel(item.code);
                        }
                      }}
                      isDisabled={isPatching}
                      aria-label={i18n.t('general.actions.cancel')}
                    />
                    <IconButton
                      icon="action/done"
                      onClick={() => {
                        if (isNotNil(item.code)) {
                          handleSave(item.code);
                        }
                      }}
                      isDisabled={isPatching}
                      aria-label={i18n.t('general.actions.save')}
                    />
                  </HStack>
                </Show>
                <Show when={!item.isEditable}>
                  <Text>
                    {formatValue(formatDateTime, item.value, item.valueType, item.options) ??
                      EMPTY_PLACEHOLDER}
                  </Text>
                </Show>
              </Box>
            </HStack>
          </Box>
        ))}
      </Box>
    </DataStatus>
  );
}
